import { selectUserConfiguration } from '@/features/domain/user'
import { useIntl } from '@/intl'
import { dateUtils } from '@/utils'
import type { DatePickerProps } from '@mui/x-date-pickers'
import { addYears, eachDayOfInterval, endOfMonth, startOfMonth, subDays } from 'date-fns/esm'
import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useRecurrenceDispatch } from '../../../../../providers/StateProvider'

export function useDatePickerProps(date: uui.domain.YYYYMMDD) {
  const dispatch = useRecurrenceDispatch()
  const userConfig = useSelector(selectUserConfiguration)
  const texts = useTexts()

  return useMemo<DatePickerProps<Date>>(() => {
    const { today } = userConfig
    const value = dateUtils.parseDate(date)
    const todayDate = dateUtils.parseDate(today)
    const minDate = startOfMonth(todayDate)
    const maxPlanningDate = addYears(minDate, 10)
    const maxDate = endOfMonth(maxPlanningDate)

    // disabled days are those before today and after the planning range
    const disabledDatesBeforeTodayInterval = {
      start: minDate,
      end: subDays(todayDate, 1),
    }

    const disabledDatesBeforeToday = dateUtils.isValidInterval(disabledDatesBeforeTodayInterval)
      ? eachDayOfInterval(disabledDatesBeforeTodayInterval)
      : []

    const disabledDatesAfterPlanningRange = eachDayOfInterval({
      start: maxPlanningDate,
      end: maxDate,
    })

    const disabledDays = [...disabledDatesBeforeToday, ...disabledDatesAfterPlanningRange].reduce<
      Record<string, number>
    >((acc, d) => {
      acc[dateUtils.convertToYYYYMMDD(d)] = 1
      return acc
    }, {})

    return {
      slotProps: {
        textField: {
          'data-testid': 'RecurrenceEndOccurrenceDate',
          'data-trackid': 'RecurrenceEndOccurrenceDate',
          inputProps: { 'data-testid': 'RecurrenceEndOccurrenceDateInput' },
        },
      },
      format: userConfig.dateFormatLong,
      label: texts.label,
      value,
      maxDate,
      minDate,
      shouldDisableDate: day => {
        return !!disabledDays[dateUtils.convertToYYYYMMDD(day)]
      },
      views: ['year', 'month', 'day'],
      sx: { height: 40, mt: '5px' },
      onChange(newSelection) {
        if (!newSelection) return
        dispatch({
          type: 'recurrenceEnd:change',
          recurrenceEnd: {
            endType: 'ON_DATE',
            endDate: dateUtils.convertToYYYYMMDD(newSelection),
          },
        })
      },
    }
  }, [userConfig, dispatch, date, texts])
}

function useTexts() {
  const { translate } = useIntl()

  const [api] = useState(() => ({
    label: translate({ id: 'recurrenceSettings.recurrenceEnd.datePicker.label' }),
  }))

  return api
}
